import { clsx, type ClassValue } from "clsx"
import { format } from "date-fns";
import { twMerge } from "tailwind-merge"

/**
 * Combines class names and merges Tailwind CSS classes efficiently.
 * @param inputs - Variable number of class values (strings, objects, arrays, etc.)
 * @returns A single string containing all merged class names
 * @example cn("px-2 py-1", "bg-red-500") // Returns: "px-2 py-1 bg-red-500"
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats YYYY-MM-DD string to long date format using browser locale.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string or empty string if invalid
 * @example formatDate("2024-01-15") // Returns: "January 15, 2024"
 * @deprecated Use `formatDateSafe` for more robust date handling
 */
export function formatDate(dateString: string): string {
  if (!dateString)
    return '';

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(
    (() => {
      const [y, m, d] = dateString.split('T')[0].split('-').map(Number);
      return new Date(y, m - 1, d);
    })()
  );
}

/**
 * Formats a date string to localized display format without timezone conversion issues.
 * Specifically designed for date-only fields like dateOfBirth and hireDate.
 * @param dateString - Date string in YYYY-MM-DD format or ISO format
 * @returns Formatted date string using browser's locale (e.g., "12/13/1997" in US)
 * @example formatDateDisplay("1997-12-13") // Returns: "12/13/1997" (US locale)
 * @example formatDateDisplay("1997-12-13T00:00:00.000Z") // Returns: "12/13/1997" (US locale)
 */
export function formatDateDisplay(dateString: string | null | undefined): string {
  if (!dateString) return "";

  const dateOnly = typeof dateString === 'string'
    ? (dateString.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? dateString.split('T')[0])
    : "";
  if (!dateOnly) return "";

  // Parse as local date components to avoid timezone shift
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return dateOnly;

  // Create date using local timezone constructor (not UTC)
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString();
}

/**
 * Safely formats dates from various input types to "MMM dd, yyyy" format.
 * @param dateString - Date object, ISO string, YYYY-MM-DD string, or null/undefined
 * @returns Formatted date string or "TBD" if invalid
 * @example formatDateSafe("2024-01-15") // Returns: "Jan 15, 2024"
 */
export function formatDateSafe(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) return "TBD";

  try {
    // If it's already a Date object, use it directly
    if (dateString instanceof Date) {
      return format(dateString, "MMM dd, yyyy");
    }

    // For string dates, check if it's in YYYY-MM-DD format (no time)
    if (
      typeof dateString === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ) {
      // Parse as local date to avoid timezone shifts
      const [year, month, day] = dateString.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "MMM dd, yyyy");
    }

    // Handle ISO strings with time by extracting the calendar date portion
    // e.g., 2025-10-01T00:00:00.000Z → treat as local date 2025-10-01
    if (
      typeof dateString === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(dateString)
    ) {
      const ymd = dateString.slice(0, 10); // YYYY-MM-DD
      const [year, month, day] = ymd.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "MMM dd, yyyy");
    }

    // Try to extract YYYY-MM-DD from any remaining string format
    if (typeof dateString === "string") {
      const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, yr, mo, dy] = match.map(Number);
        return format(new Date(yr, mo - 1, dy), "MMM dd, yyyy");
      }
    }

    // Last resort — may still shift in edge cases
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch (error) {
    console.error("Date formatting error:", error);
    return "TBD";
  }
}

/**
 * Outcome value → human-friendly label mapping
 */
export const OUTCOME_LABELS: Record<string, string> = {
  successful: "Successful",
  needs_follow_up: "Needs Follow-up",
  no_response: "No Response",
  resolved: "Resolved",
  escalated: "Escalated",
};

/**
 * Formats a date into full month format, e.g., "January 15, 2024".
 * Accepts Date, ISO string, or YYYY-MM-DD.
 */
export function formatDateLong(
  dateInput: string | Date | null | undefined
): string {
  if (!dateInput) return "TBD";
  try {
    if (dateInput instanceof Date) {
      return format(dateInput, "MMMM d, yyyy");
    }
    if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [y, m, d] = dateInput.split("-").map(Number);
      return format(new Date(y, m - 1, d), "MMMM d, yyyy");
    }
    // Try to extract YYYY-MM-DD from any remaining string format
    if (typeof dateInput === "string") {
      const match = dateInput.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, yr, mo, dy] = match.map(Number);
        return format(new Date(yr, mo - 1, dy), "MMMM d, yyyy");
      }
    }

    return format(new Date(dateInput), "MMMM d, yyyy");
  } catch (e) {
    return "TBD";
  }
}

/**
 * The first of next month as YYYY-MM-DD, in the browser's calendar. Built from
 * local parts on purpose: `.toISOString()` on a local midnight lands on the
 * previous day east of UTC (in Tokyo, 2027-01-01 became 2026-12-31).
 */
export function firstOfNextMonthYmd(now: Date = new Date()): string {
  return toYmdLocal(new Date(now.getFullYear(), now.getMonth() + 1, 1))!;
}

/**
 * A calendar date as YYYY-MM-DD for an API param. Strings keep their own date
 * part (`2027-01-01T00:00:00.000Z` → `2027-01-01`); Date objects (date pickers
 * hand back local midnight) are read in local time. Use this instead of
 * `.toISOString().split("T")[0]`, which shifts local midnight a day back east of UTC.
 */
export function toYmdLocal(value: string | Date | null | undefined): string | undefined {
  const d = parseDateLocal(value);
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Safely parses a date string (YYYY-MM-DD or ISO) into a Date object using local timezone.
 * This avoids timezone conversion issues when parsing date-only strings.
 * @param dateString - Date string in YYYY-MM-DD format or ISO format
 * @returns Date object in local timezone, or null if invalid
 */
export function parseDateLocal(dateString: string | Date | null | undefined): Date | null {
  if (!dateString) return null;
  
  // Handle Date objects
  if (dateString instanceof Date) {
    // Extract YYYY-MM-DD from the date object
    const year = dateString.getFullYear();
    const month = dateString.getMonth() + 1;
    const day = dateString.getDate();
    return new Date(year, month - 1, day);
  }
  
  const dateOnly = typeof dateString === 'string'
    ? (dateString.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? dateString.split('T')[0])
    : null;
  if (!dateOnly || !/^\d{4}-\d{2}-\d{2}$/.test(dateOnly.slice(0, 10))) return null;
  
  const [year, month, day] = dateOnly.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  
  // Create date using local timezone constructor (not UTC)
  return new Date(year, month - 1, day);
}

/**
 * Formats US phone numbers into a readable form.
 * - 10 digits: (XXX) XXX-XXXX
 * - 11 digits starting with 1: +1 (XXX) XXX-XXXX
 * - 7 digits: XXX-XXXX
 * Otherwise returns the original input.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = String(phone).replace(/\D+/g, "");

  if (digits.length === 10) {
    const area = digits.slice(0, 3);
    const mid = digits.slice(3, 6);
    const last = digits.slice(6);
    return `(${area}) ${mid}-${last}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    const area = digits.slice(1, 4);
    const mid = digits.slice(4, 7);
    const last = digits.slice(7);
    return `+1 (${area}) ${mid}-${last}`;
  }

  if (digits.length === 7) {
    const mid = digits.slice(0, 3);
    const last = digits.slice(3);
    return `${mid}-${last}`;
  }

  // Fallback: return as provided when it's not a standard US pattern
  return String(phone);
}

/**
 * Dictionary for source formatting and validation
 */
export const SOURCE_OPTIONS = {
  referral: "Referral",
  website: "Website", 
  cold_call: "Cold Call",
  marketing: "Marketing Campaign"
} as const;

/**
 * Formats source values to display-friendly text.
 * @param source - Source value from the database
 * @returns Formatted source string or "Unknown" if invalid
 * @example formatSource("cold_call") // Returns: "Cold Call"
 */
export function formatSource(source: string | null | undefined): string {
  if (!source) return "Unknown";
  return SOURCE_OPTIONS[source as keyof typeof SOURCE_OPTIONS] || source;
}

/**
 * Format number to US currency
 */
export function formatCurrency(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  // Force "$" instead of locale-specific "US$" while still formatting in USD.
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
  }).format(safe);
}

/**
 * Gets current datetime in user's local timezone formatted for datetime-local input
 * @returns String in format YYYY-MM-DDTHH:mm (e.g., "2024-01-15T14:30")
 * @example getCurrentLocalDateTime() // Returns: "2024-01-15T14:30"
 */
export function getCurrentLocalDateTime(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Converts a Date object to local datetime string for datetime-local input
 * @param date - Date object to convert
 * @returns String in format YYYY-MM-DDTHH:mm (e.g., "2024-01-15T14:30")
 * @example toLocalDateTime(new Date("2024-01-15T14:30:00Z")) // Returns: "2024-01-15T09:30" (if user is in EST)
 */
export function toLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats Social Security Number to XXX-XX-XXXX format
 * @param ssn - SSN string with or without formatting
 * @returns Formatted SSN string or empty string if invalid
 * @example formatSSN("123456789") // Returns: "123-45-6789"
 * @example formatSSN("123-45-6789") // Returns: "123-45-6789"
 */
export function formatSSN(ssn: string | null | undefined): string {
  if (!ssn) return "";

  // Remove all non-digit characters
  const digits = String(ssn).replace(/\D+/g, "");

  // Check if we have exactly 9 digits
  if (digits.length !== 9) return ssn || "";

  // Format as XXX-XX-XXXX
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Masks an SSN down to the last 4 digits for display.
 * Also accepts an already-masked API value (DEV-351: "***-**-6789", "XXX-XX-6789", "•••-••-6789").
 * @example maskSSN("123456789") // Returns: "***-**-6789"
 * @example maskSSN("***-**-6789") // Returns: "***-**-6789"
 */
export function maskSSN(ssn: string | null | undefined): string {
  if (!ssn) return "";
  const text = String(ssn).trim();
  const digits = text.replace(/\D+/g, "");
  if (digits.length === 9) return `***-**-${digits.slice(5)}`;
  // Already masked: mask chars / separators followed by the last 4 digits
  const masked = text.match(/^[*xX•\-\s]+(\d{4})$/);
  if (masked) return `***-**-${masked[1]}`;
  // Fully masked by the server (stored value had < 4 digits)
  if (/^[*xX•\-\s]+$/.test(text) && /[*xX•]/.test(text)) return "***-**-****";
  return "";
}

/**
 * maskSSN in the Form 1095-C style ("XXX-XX-6789"). Accepts already-masked API
 * values; fully-masked / unrecognised input returns "".
 */
export function maskSSNWithX(ssn: string | null | undefined): string {
  const m = maskSSN(ssn);
  return /\d{4}$/.test(m) ? m.replace(/\*/g, "X") : "";
}

/** Capitalizes the first letter of a single-word value (e.g. "male" -> "Male"). */
export function capitalizeFirst(value: string | null | undefined): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Formats SSN input value as user types (auto-formatting)
 * @param value - Current input value
 * @returns Formatted SSN string with hyphens inserted automatically
 * @example formatSSNInput("123") // Returns: "123"
 * @example formatSSNInput("12345") // Returns: "123-45"
 * @example formatSSNInput("123456789") // Returns: "123-45-6789"
 */
export function formatSSNInput(value: string): string {
  if (!value) return "";

  // Remove all non-digit characters
  const digits = value.replace(/\D+/g, "");

  // Limit to 9 digits
  const limited = digits.slice(0, 9);

  // Format based on length
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 5) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  } else {
    return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
  }
}

/**
 * Human-readable file size, e.g. 1536 → "1.5 KB". Returns `emptyText` (default
 * "0 Bytes") for null/0. Canonical helper — replaces the per-component copies.
 */
export function formatFileSize(
  bytes: number | null | undefined,
  opts: { emptyText?: string } = {},
): string {
  if (bytes == null || bytes === 0) return opts.emptyText ?? "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}